import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { markTourCompleted, markTourStarted } from "./tourStorage";
import { getTour } from "./tourRegistry";

const TourContext = createContext(null);

export function useTourContext() {
  return useContext(TourContext);
}

export default function TourProvider({ children }) {
  const [activeTourKey, setActiveTourKey] = useState(null);
  const [run, setRun] = useState(false);
  const [tourSteps, setTourSteps] = useState([]);
  const [runId, setRunId] = useState(0);
  const activeTourKeyRef = useRef(null);

  const stopTour = useCallback((status = "completed") => {
    const key = activeTourKeyRef.current;
    if (key) {
      const tour = getTour(key);
      if (tour) {
        markTourCompleted(tour.key, tour.version, status);
      }
    }
    activeTourKeyRef.current = null;
    setActiveTourKey(null);
    setRun(false);
    setTourSteps([]);
  }, []);

  const startTour = useCallback((tourKey, steps) => {
    const tour = getTour(tourKey);
    if (tour) {
      markTourStarted(tour.key, tour.version);
    }

    activeTourKeyRef.current = tourKey;
    setActiveTourKey(tourKey);
    setRun(false);
    setTourSteps([]);

    const start = () => {
      setRunId((prev) => prev + 1);
      setRun(true);
      setTourSteps(steps);
    };

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(start);
      return;
    }

    setTimeout(start, 0);
  }, []);

  const handleJoyrideCallback = useCallback(
    (data) => {
      const { status, type, action } = data;

      if (status === "finished") {
        stopTour("completed");
        return;
      }

      if (status === "skipped") {
        stopTour("skipped");
        return;
      }

      if (type === "step:after" && action === "close") {
        stopTour("skipped");
      }
    },
    [stopTour]
  );

  const value = {
    activeTourKey,
    activeTourKeyRef,
    run,
    runId,
    tourSteps,
    startTour,
    stopTour,
    handleJoyrideCallback,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
